// The session detail: replay and read a past sitting.
//
// For each question answered, we show the audio (streamed from Storage via
// getDownloadURL) and its transcript. Light editing lives here too: the owner
// can rename the session and fix a garbled transcript line. This screen reloads
// on every visit, so a one-shot getDocs is enough — no live subscription.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import {
  db,
  ensureSignedIn,
  saveSessionTitle,
  saveTranscript,
  storage,
} from "./firebase";
import { formatDate, formatTime, latestTakes, type Take } from "./session-data";

const bodyEl = document.getElementById("detailBody") as HTMLElement;

export async function renderSessionDetail(sessionId: string): Promise<void> {
  bodyEl.replaceChildren(loadingLine("Loading this session…"));

  let uid: string;
  let sessionData: Record<string, unknown> | undefined;
  let takes: Take[];
  try {
    uid = await ensureSignedIn();
    const [sessionSnap, recSnap] = await Promise.all([
      getDoc(doc(db, "sessions", sessionId)),
      getDocs(
        query(
          collection(db, "recordings"),
          // Owner filter matches the M3 privacy rule (reads are scoped to
          // createdBy); a non-owner simply gets an empty list here.
          where("createdBy", "==", uid),
          where("sessionId", "==", sessionId),
        ),
      ),
    ]);
    sessionData = sessionSnap.exists() ? sessionSnap.data() : undefined;
    takes = latestTakes(recSnap.docs);
  } catch (err) {
    bodyEl.replaceChildren(
      loadingLine(`Couldn't load this session: ${(err as Error).message}`),
    );
    return;
  }

  if (!sessionData) {
    bodyEl.replaceChildren(loadingLine("This session couldn't be found."));
    return;
  }

  const isOwner = sessionData.createdBy === uid;

  bodyEl.replaceChildren();
  bodyEl.append(buildHeader(sessionId, sessionData, isOwner));

  if (takes.length === 0) {
    bodyEl.append(loadingLine("This session has no answers yet."));
    return;
  }

  const list = document.createElement("ul");
  list.className = "detail-list";
  for (const take of takes) list.append(buildAnswerCard(take, isOwner));
  bodyEl.append(list);
}

// ---- Header: date + editable title ----

function buildHeader(
  sessionId: string,
  data: Record<string, unknown>,
  isOwner: boolean,
): HTMLElement {
  const header = document.createElement("header");
  header.className = "detail-header";

  const dateStr = `${formatDate(data.startedAt)} · ${formatTime(data.startedAt)}`;
  const dateFallback = formatDate(data.startedAt);
  const title = ((data.title as string | undefined) ?? "").trim();

  if (isOwner) {
    // A small label makes it clear this is something you fill in, not a static
    // heading — untitled sittings otherwise looked identical to plain text.
    const label = document.createElement("label");
    label.className = "detail-title-label";
    label.textContent = title ? "Session name" : "Name this session";

    const input = document.createElement("input");
    input.id = "sessionTitleInput";
    label.htmlFor = input.id;
    input.className = "detail-title-input";
    input.type = "text";
    input.value = title;
    input.placeholder = "Add a name…";
    input.setAttribute("aria-label", "Session name");
    input.maxLength = 120;

    const saved = document.createElement("span");
    saved.className = "detail-saved";
    saved.hidden = true;

    let lastSaved = title;
    const commit = async () => {
      const next = input.value.trim();
      if (next === lastSaved) return;
      try {
        await saveSessionTitle(sessionId, next);
        lastSaved = next;
        flash(saved, "Saved");
      } catch (err) {
        flash(saved, `Couldn't save: ${(err as Error).message}`, true);
      }
    };
    input.addEventListener("blur", () => void commit());
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") input.blur();
    });

    const meta = document.createElement("p");
    meta.className = "detail-meta";
    meta.textContent = dateStr;

    header.append(label, input, meta, saved);
  } else {
    const h = document.createElement("h1");
    h.className = "detail-title";
    h.textContent = title || dateFallback;

    const meta = document.createElement("p");
    meta.className = "detail-meta";
    meta.textContent = dateStr;

    header.append(h, meta);
  }
  return header;
}

// ---- One answer: question, audio, transcript (editable) ----

function buildAnswerCard(take: Take, isOwner: boolean): HTMLLIElement {
  const { id, data } = take;
  const li = document.createElement("li");
  li.className = "detail-item";

  const qEl = document.createElement("p");
  qEl.className = "detail-q";
  qEl.textContent = (data.question as string) || "Question";
  li.append(qEl);

  const audio = document.createElement("audio");
  audio.className = "detail-audio";
  audio.controls = true;
  audio.preload = "none";
  const storagePath = data.storagePath as string | undefined;
  if (storagePath) {
    getDownloadURL(ref(storage, storagePath))
      .then((url) => (audio.src = url))
      .catch(() => {
        audio.hidden = true;
      });
  } else {
    audio.hidden = true;
  }
  li.append(audio);

  li.append(buildTranscript(id, data, isOwner));
  return li;
}

function buildTranscript(
  docId: string,
  data: Record<string, unknown>,
  isOwner: boolean,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "detail-transcript";

  const transcribed = data.status === "transcribed";
  const text = (data.transcript as string) || "";

  const p = document.createElement("p");
  p.className = "detail-a";
  if (transcribed) {
    p.textContent = text || "(empty transcript)";
  } else if (data.status === "error") {
    p.textContent = "Couldn't transcribe this one.";
    p.classList.add("muted");
  } else {
    p.textContent = "Still transcribing…";
    p.classList.add("muted");
  }
  wrap.append(p);

  // Only a landed transcript belonging to the owner can be corrected.
  if (isOwner && transcribed) {
    const edit = document.createElement("button");
    edit.type = "button";
    edit.className = "detail-edit";
    edit.textContent = "Fix this transcript";
    edit.addEventListener("click", () =>
      enterEditMode(wrap, p, edit, docId, text),
    );
    wrap.append(edit);
  }
  return wrap;
}

/** Swap the transcript paragraph for a textarea with Save / Cancel. */
function enterEditMode(
  wrap: HTMLElement,
  p: HTMLParagraphElement,
  editBtn: HTMLButtonElement,
  docId: string,
  current: string,
) {
  editBtn.hidden = true;
  p.hidden = true;

  const form = document.createElement("div");
  form.className = "detail-edit-form";

  const textarea = document.createElement("textarea");
  textarea.className = "detail-textarea";
  textarea.value = p.textContent === "(empty transcript)" ? "" : current;
  textarea.rows = Math.min(10, Math.max(3, textarea.value.split("\n").length + 1));

  const actions = document.createElement("div");
  actions.className = "detail-edit-actions";

  const save = document.createElement("button");
  save.type = "button";
  save.className = "detail-btn detail-btn-primary";
  save.textContent = "Save";

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "detail-btn";
  cancel.textContent = "Cancel";

  const note = document.createElement("span");
  note.className = "detail-saved";
  note.hidden = true;

  const close = () => {
    form.remove();
    p.hidden = false;
    editBtn.hidden = false;
  };

  cancel.addEventListener("click", close);
  save.addEventListener("click", async () => {
    const next = textarea.value.trim();
    save.disabled = true;
    cancel.disabled = true;
    try {
      await saveTranscript(docId, next);
      p.textContent = next || "(empty transcript)";
      close();
    } catch (err) {
      save.disabled = false;
      cancel.disabled = false;
      flash(note, `Couldn't save: ${(err as Error).message}`, true);
    }
  });

  actions.append(save, cancel, note);
  form.append(textarea, actions);
  wrap.append(form);
  textarea.focus();
}

// ---- small helpers ----

function loadingLine(text: string): HTMLElement {
  const p = document.createElement("p");
  p.className = "session-empty";
  p.textContent = text;
  return p;
}

let flashTimer: number | null = null;
function flash(el: HTMLElement, text: string, isError = false) {
  el.textContent = text;
  el.hidden = false;
  el.classList.toggle("is-error", isError);
  if (flashTimer !== null) clearTimeout(flashTimer);
  flashTimer = window.setTimeout(() => {
    el.hidden = true;
  }, 2400);
}
